import React from "react";

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the whole app.
 *
 * Props:
 * - children: React nodes to wrap
 * - onError: Optional callback when an error is caught
 * - fallback: Optional custom fallback UI component
 * - routeLevel: If true, shows a more compact error UI suitable for route-level boundaries
 * - moduleName: Optional module name for better error context (e.g., "Research", "Budget")
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });

    if (typeof this.props.onError === "function") {
      this.props.onError(error, errorInfo);
    }

    // Log to external service in production
    if (import.meta.env.PROD && window.Sentry) {
      window.Sentry.captureException(error, { extra: errorInfo });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          retry: this.handleRetry,
        });
      }

      // Route-level compact error UI
      if (this.props.routeLevel) {
        return (
          <div className="p-8 max-w-2xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 space-y-4">
              <div className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <h2 className="text-lg font-semibold text-red-800 dark:text-red-200">
                    {this.props.moduleName
                      ? `Error in ${this.props.moduleName}`
                      : "Something went wrong"}
                  </h2>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    This section encountered an error. The rest of the
                    application is still working.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
                  onClick={this.handleRetry}
                >
                  Try again
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  onClick={() => (window.location.href = "/")}
                >
                  Go to Dashboard
                </button>
              </div>
              {import.meta.env.DEV && this.state.error && (
                <details className="mt-4">
                  <summary className="text-sm text-red-700 dark:text-red-300 cursor-pointer hover:underline">
                    Show error details
                  </summary>
                  <pre className="mt-2 text-xs bg-slate-900/90 text-slate-100 p-3 rounded-lg overflow-x-auto max-h-48">
                    {String(this.state.error?.stack || this.state.error)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        );
      }

      // Full-page error UI (root-level)
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="content-shell max-w-xl text-center space-y-4">
            <h1 className="text-2xl font-bold text-red-700 dark:text-red-300">
              Something went wrong
            </h1>
            <p className="text-gray-700 dark:text-gray-200">
              An unexpected error occurred. You can try again or refresh the
              page.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                className="btn-primary"
                onClick={this.handleRetry}
              >
                Try again
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => window.location.reload()}
              >
                Reload page
              </button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-left text-xs bg-slate-900/80 text-slate-100 p-3 rounded-lg overflow-x-auto">
                {String(this.state.error?.stack || this.state.error)}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Higher-order component to wrap a component with an error boundary.
 * Useful for wrapping route components.
 *
 * Usage:
 *   const SafeBudgetOverview = withErrorBoundary(BudgetOverview, { moduleName: 'Budget' });
 */
export function withErrorBoundary(WrappedComponent, options = {}) {
  const { moduleName, routeLevel = true, onError } = options;

  return function WithErrorBoundaryWrapper(props) {
    return (
      <ErrorBoundary
        routeLevel={routeLevel}
        moduleName={moduleName}
        onError={onError}
      >
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

/**
 * Component to wrap route elements with error boundary.
 * Provides a cleaner API for route definitions.
 *
 * Usage:
 *   <Route path="/budget" element={<RouteErrorBoundary moduleName="Budget"><BudgetOverview /></RouteErrorBoundary>} />
 */
export function RouteErrorBoundary({ children, moduleName, onError }) {
  return (
    <ErrorBoundary routeLevel moduleName={moduleName} onError={onError}>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
